/**
 * 3D 분석 — 생육 사진 올리기 (POST /api/growth/photos/manual).
 *
 * 고른 장비 · 날짜(기본 오늘, 한국 시간)로 사진을 올리면 서버가 장비 업로드와 같은 자리에 저장하고 기록을 만든 뒤
 * LRM 에 바로 3D 처리를 맡긴다. 결과(3D · 초장 · 엽면적)는 처리가 끝나면 이 화면에 나온다 (보통 5~10 분).
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sym } from '../../ui';
import { uploadGrowthPhoto, type ManualUploadResult } from '../../api/growth';

/** 한국 시간 오늘 'YYYY-MM-DD' — 브라우저 시간대와 무관 */
export function kstToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  background: 'rgba(0,0,0,.34)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const field: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container)',
  color: 'var(--md-sys-color-on-surface)',
  font: '500 13px/1 var(--gs-font-kr)',
  boxSizing: 'border-box',
};

const label: React.CSSProperties = {
  display: 'block',
  margin: '12px 0 6px',
  font: '600 12px/16px var(--gs-font-kr)',
  color: 'var(--md-sys-color-on-surface-variant)',
};

export function UploadPhotoPopup({
  deviceId,
  deviceName,
  onUploaded,
  onClose,
}: {
  deviceId: string;
  deviceName: string;
  onUploaded: (r: ManualUploadResult) => void;
  onClose: () => void;
}): React.ReactElement {
  const today = kstToday();
  const [date, setDate] = useState(today);
  const [file, setFile] = useState<File | null>(null);
  const m = useMutation({
    mutationFn: () => uploadGrowthPhoto(deviceId, file as File, date),
    onSuccess: (r) => onUploaded(r),
  });

  return (
    <div role="dialog" onClick={onClose} style={overlay}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(400px, 100%)',
          padding: 20,
          borderRadius: 20,
          background: 'var(--md-sys-color-surface-container-lowest)',
          border: '1px solid var(--md-sys-color-outline-variant)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            font: '700 16px/22px var(--gs-font-kr)',
          }}
        >
          <Sym name="add_photo_alternate" size={20} />
          생육 사진 올리기
        </div>
        <div
          style={{
            marginTop: 4,
            font: '500 12px/18px var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface-variant)',
          }}
        >
          {deviceName} · 올리면 3D 모델링을 바로 시작합니다 (보통 5~10분). 같은 날짜에 다시 올리면
          사진이 바뀝니다.
        </div>

        <label style={label} htmlFor="g3d-up-date">
          촬영 날짜
        </label>
        <input
          id="g3d-up-date"
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          style={field}
        />

        <label style={label} htmlFor="g3d-up-file">
          사진 (JPG · PNG, 20MB 이하)
        </label>
        <input
          id="g3d-up-file"
          type="file"
          accept="image/jpeg,image/png"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ ...field, height: 'auto', padding: 8 }}
        />

        {m.isError && (
          <div
            style={{
              marginTop: 12,
              font: '500 12px/18px var(--gs-font-kr)',
              color: 'var(--md-sys-color-error)',
            }}
          >
            {(m.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 9999,
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid var(--md-sys-color-outline)',
              color: 'var(--md-sys-color-on-surface)',
              font: '600 13px/1 var(--gs-font-kr)',
            }}
          >
            취소
          </button>
          <button
            type="button"
            disabled={!file || !date || m.isPending}
            onClick={() => m.mutate()}
            style={{
              height: 36,
              padding: '0 16px',
              borderRadius: 9999,
              cursor: !file || m.isPending ? 'default' : 'pointer',
              background: 'var(--md-sys-color-primary)',
              border: 'none',
              color: 'var(--md-sys-color-on-primary)',
              font: '600 13px/1 var(--gs-font-kr)',
              opacity: !file || !date || m.isPending ? 0.5 : 1,
            }}
          >
            {m.isPending ? '올리는 중…' : '올리고 3D 처리'}
          </button>
        </div>
      </div>
    </div>
  );
}
